import { defineStore } from 'pinia'
import { ref } from 'vue'

import { isPWARuntime } from '@/components_new/PWAInstall/utils'
import { useConfig } from '@/composables/config'
import { useLoggerStore } from '@/store/logger'
import { useUserStore } from '@/store/user-store/user-store'

import { useAppStore } from './app-store'

export const useGoogleAuth = defineStore('googleAuth', () => {
    const { VUE_APP_GAUTH_REDIRECT, VUE_APP_GAUTH_CLID } = useConfig()
    const loggerStore = useLoggerStore()
    const userStore = useUserStore()
    const appStore = useAppStore()
    const onFinishResolver = ref<((result: void) => void) | null>(null)
    const initialized = ref<boolean>(false)
    const promptDisplayed = ref<boolean>(false)

    function onFinishSignIn(onFinish: (() => void) | undefined) {
        return new Promise<void>((resolve) => {
            onFinishResolver.value = () => {
                onFinish?.()
                resolve()
            }
        })
    }

    function setPrompt() {
        // invoke prompt only for web
        if (window.google && !isPWARuntime() && !userStore.isAuthorized) {
            window.google.accounts.id.prompt((notification) => {
                // https://developers.google.com/identity/gsi/web/reference/js-reference#PromptMomentNotification
                if (typeof notification.isDisplayed === 'function') {
                    promptDisplayed.value = notification.isDisplayed()
                } else {
                    promptDisplayed.value = notification.getMomentType() === 'display'
                }
            })
        }
    }

    function cancelPrompt() {
        window.google.accounts.id.cancel()
    }

    async function generateNonce(): Promise<{ hashedNonce: string; rawNonce: string }> {
        const rawNonce = crypto.randomUUID().replace(/-/g, '')
        const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(rawNonce))
        const hashedNonce = [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('')
        return { hashedNonce, rawNonce }
    }

    /* should be used only once as google initialize should be invoked only once! -> see more https://developers.google.com/identity/gsi/web/reference/js-reference */
    async function initGlobalSignIn() {
        if (!window.google || initialized.value) {
            return
        }

        const { hashedNonce, rawNonce } = await generateNonce()

        const commonOptions = {
            client_id: VUE_APP_GAUTH_CLID,
            auto_select: true,
            cancel_on_tap_outside: false,
            use_fedcm_for_prompt: true,
            nonce: hashedNonce,
        }

        const temporaryDisableRedirectInFrames = true
        if (appStore.isIframe && !temporaryDisableRedirectInFrames) {
            // When playgama is embedded on 3d party domain with COOP enabled
            // GSI cannot communicate from popup back to opener window
            window.google.accounts.id.initialize({
                ...commonOptions,
                ux_mode: 'redirect',
                login_uri: VUE_APP_GAUTH_REDIRECT,
            })
        } else {
            window.google.accounts.id.initialize({
                ...commonOptions,
                ux_mode: 'popup',
                callback: ({ credential }) => handleAuthentication(credential, rawNonce),
            })
        }
        initialized.value = true
    }

    const handleAuthentication = async (credential: string, nonce: string) => {
        await fetch(VUE_APP_GAUTH_REDIRECT, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
                credential,
                nonce,
            }),
        })

        await userStore.fetchUserInfo()
        loggerStore.logEvent({
            event: 'custom_event',
            eventName: 'login',
            action: userStore.isAuthorized ? 'success' : 'fail',
        })
        if (promptDisplayed.value && userStore.isAuthorized) {
            promptDisplayed.value = false
        }
        if (onFinishResolver.value) {
            onFinishResolver.value()
            onFinishResolver.value = null
        }
    }
    return {
        initGlobalSignIn,
        setPrompt,
        cancelPrompt,
        onFinishSignIn,
        initialized,
        promptDisplayed,
    }
})
